import Image from 'next/image';
import cn from 'clsx';
import {useRef, useState} from 'react';
import {Controller, FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import {IoMdCloseCircle} from '@assets/react-icons/io';
import {MdCloudUpload} from '@assets/react-icons/md';
import {serialize} from 'object-to-formdata';
import {Field} from '@components/form-fields/Field';
import {TextInput} from '@components/form-fields/Inputs/TextInput';
import {SpinnerV3} from '@components/widgets/Spinners/LoadingCircle';
import {ChevronDown, ChevronRight} from '@components/widgets/Icons';

export const UploadMediaForProductTab = ({
                                           sku,
                                           mutateMedia,
                                           title,
                                           subtitle,
                                           showUpload,
                                           setShowUpload
                                         }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [unsupportedFiles, setUnsupportedFiles] = useState([]);
  const [mainSrcImage, setMainSrcImage] = useState('');
  const [photoIndex, setPhotoIndex] = useState(0);
  const [sanitizedImages, setSanitizedImages] = useState([]);
  const [imageCaption, setImageCaption] = useState('');
  const methods = useForm({
    defaultValues: {
      files: []
    }
  });

  const onSubmit = async (formData) => {
    setSubmitting(true);
    setFormError('');
    setFormMessage('');

    try {
      const justAltTexts = formData?.files?.map((file) => file?.description ?? '');

      const processedFiles = formData?.files?.map((file, index) => {
        const oneFile = {
          index: index + 1,
          file: file?.file,
          description: justAltTexts[index] ?? '',
          previewSrc: file?.previewSrc ?? ''
        };
        return oneFile;
      });

      const submitData = {
        files: processedFiles,
        sku
      };

      const response = await fetch('/api/product/media', {
        method: 'POST',
        body: serialize(submitData)
      });
      const responseJson = await response.json();

      if (responseJson?.success) {
        await mutateMedia();
        setFormMessage('✅ Thank you for submitting your media');
        methods.reset();
        setSubmitting(false);
      } else {
        setFormError('Ooops. Something is not right. Please try again.');
        setSubmitting(false);
      }
    } catch (e) {
      console.error(e?.message);
      console.error(e?.response?.data);
      setSubmitting(false);
      setFormError(`Error: ${e}`);
    }
  };

  const currentFiles = methods.watch('files');
  const disableSubmit = submitting || currentFiles?.length === 0 || unsupportedFiles?.length;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods?.handleSubmit(onSubmit)}
        className={'text-black border bg-neutral-100 rounded p-2 font-sans relative w-full'}>
        {(!formMessage || formMessage === '') && (
          <>
            {title && (
              <div
                className="flex flex-col md:flex-row justify-center items-center md:justify-between w-full cursor-pointer"
                onClick={() => setShowUpload(!showUpload)}>
                <div
                  className="text-center md:text-left font-bebas tracking-wide text-xl select-none">
                  {title}
                </div>
                {subtitle && !showUpload && <div className={'md:hidden text-xs'}>{subtitle}</div>}
                <div className={'hidden md:block'}>
                  {showUpload && <ChevronDown />}
                  {!showUpload && <ChevronRight />}
                </div>
              </div>
            )}

            {showUpload && (
              <div className={'slideInUpSmall'}>
                <Controller
                  name="files"
                  control={methods?.control}
                  render={({field}) => {
                    return <DragAndDropFileControl
                      unsupportedFiles={unsupportedFiles}
                      setUnsupportedFiles={setUnsupportedFiles}
                      setMainSrcImage={setMainSrcImage}
                      setSanitizedImages={setSanitizedImages}
                      setPhotoIndex={setPhotoIndex}
                      setImageCaption={setImageCaption}
                      {...field}
                    />;
                  }}
                />
              </div>
            )}
          </>
        )}

        {currentFiles?.length > 0 && (
          <div className="py-1 mx-auto text-center">
            <button
              type="submit"
              disabled={disableSubmit}
              className={cn(
                'cursor-pointer py-2 px-4 block mt-1 font-oswald uppercase w-full text-center text-white rounded',
                {
                  'bg-neutral-200': disableSubmit,
                  'bg-neutral-800': !disableSubmit
                }
              )}>
              Submit Media
            </button>
          </div>
        )}

        {submitting && (
          <div
            className={
              'absolute bg-neutral-100 bg-opacity-95 w-full h-full top-0 left-0 flex flex-col justify-center items-center'
            }>
            <SpinnerV3 />
          </div>
        )}

        {formMessage && (
          <div className={'text-center slideInUp h-32 md:h-40 flex items-center justify-center'}>
            {formMessage}
          </div>
        )}
      </form>
    </FormProvider>
  );
};


export const DragAndDropFileControl = ({
                                         name,
                                         unsupportedFiles,
                                         setUnsupportedFiles,
                                         setMainSrcImage,
                                         setSanitizedImages,
                                         setPhotoIndex,
                                         setImageCaption
                                       }) => {
  const [dragging, setDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const fileInputRef = useRef(null);
  const dragEventCounter = useRef(0);

  const {register, control, formState} = useFormContext();
  const {fields: addedImages, append, remove} = useFieldArray({name, control});

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragEventCounter.current++;
    if (dragEventCounter.current === 1) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragEventCounter.current--;
    if (dragEventCounter.current === 0) {
      setDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragEventCounter.current = 0;
    setDragging(false);
    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFocus = (index) => {
    setActiveIndex(index);
  };

  const handleBlur = () => {
    setActiveIndex(null);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleChange = (e) => {
    const files = e.target.files;
    handleFiles(files);
  };

  const acceptedImageTypes = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/heif',
    'image/heic',
    'image/bmp',
    'image/tiff',
    'image/svg+xml'
  ];

  const handleFileFilter = (inputFile) => {
    if (acceptedImageTypes.includes(inputFile.type)) {
      return true;
    }
    return false;
  };

  const checkImageQuality = (file, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = document.createElement('img');
      img.onload = () => {
        const isLowQuality =
          img.width < 500 ||
          img.height < 500 ||
          file.size < 100 * 1024;
        callback(isLowQuality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (files) => {
    if (files?.length) {
      const inputFiles = Array.from(files);
      inputFiles.forEach((inputFile) => {
        const new_key = `${inputFile?.name}-${inputFile?.size}`;
        const isDuplicate = addedImages.some((field) => {
          const key = `${field?.file?.name}-${field.file?.size}`;
          return key === new_key;
        });

        const isValidFile = handleFileFilter(inputFile);
        if (!isValidFile) {
          setUnsupportedFiles((prev) => [
            ...prev,
            {
              file: inputFile,
              error_reason: 'Invalid File Type'
            }
          ]);
          return;
        }

        checkImageQuality(inputFile, (isLowQuality) => {
          if (isLowQuality) {
            setUnsupportedFiles((prev) => [
              ...prev,
              {
                file: inputFile,
                error_reason: 'Image too small or low quality'
              }
            ]);
            return;
          }

          if (!isDuplicate) {
            append({file: inputFile, description: '', previewSrc: URL.createObjectURL(inputFile)});
          }
        });
      });
    }
  };

  return (
    <>
      <ul className="text-left mb-4 list-disc list-inside bg-neutral-200 p-4 rounded-lg">
        <li>Image must be at least 500x500 pixels</li>
        <li>Image size must be less than 100KB</li>
        <li>
          Accepted formats: JPG, JPEG, PNG, GIF, WEBP, AVIF, HEIF, HEIC, BMP, TIFF, SVG
        </li>
      </ul>
      <div
        className={cn(
          `p-2 pb-0 lg:p-4 w-100 h-32 md:h-40 text-center cursor-pointer border-2 border-neutral-800 border-dashed rounded-lg`,
          {
            'border-sky-500 bg-neutral-200': dragging,
            'bg-neutral-100 md:border-neutral-300': !dragging
          }
        )}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={'flex flex-col mdda:p-4 select-none'}>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            onChange={handleChange}
            accept={acceptedImageTypes.join(',')}
          />
          <div className={'flex justify-center'}>
            <MdCloudUpload size={48} />
          </div>
          {dragging && <p>Drop the files here ...</p>}
          {!dragging && (
            <>
              <div className={'text-xl uppercase font-bebas tracking-wider text-center'}>
                Upload Your Own Media
              </div>
              <div>
                <span className={'hidden md:inline-block'}>Drag 'n drop some files here</span>
                <span className={'inline-block md:hidden'}>Tap to select</span>
              </div>
            </>
          )}
        </div>
      </div>

      {unsupportedFiles?.length > 0 && (
        <div className="pt-1.5 space-y-4 my-4 px-2">
          {unsupportedFiles.map((unsupportedFile, index) => (
            <div key={index} className="flex gap-2 items-center mb-4">
              <div className="relative" style={{width: '87px', height: '87px'}}>
                <div className="h-full border border-rose-700 rounded overflow-hidden">
                  <Image
                    src={URL.createObjectURL(unsupportedFile.file)}
                    alt={`Invalid file ${index + 1}`}
                    className="object-contain"
                    layout="fill"
                  />
                  <div
                    className="absolute -top-3 -right-2 cursor-pointer rounded-full bg-white border-black"
                    onClick={() =>
                      setUnsupportedFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <IoMdCloseCircle size={20} />
                  </div>
                </div>
              </div>
              <div className="flex pl-1 border-l-4 border-rose-700 items-center" style={{
                minHeight: '35px'
              }}>
                <div
                  className="bg-rose-100 text-rose-500 text-s border border-rose-400 rounded">
                  <span className="m-1.5">{unsupportedFile?.error_reason}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {addedImages?.length > 0 && (
        <div className={`space-y-4 my-4 px-2`}>
          {addedImages?.map((addedImage, index) => {
            const isActive = activeIndex === index;
            return (
              <div key={addedImage?.id} className="flex gap-2 mb-4">
                <div className="relative" style={{minWidth: '87px', minHeight: '87px'}}>
                  <div className="h-full border border-neutral-700 rounded overflow-hidden">
                    <Image
                      src={addedImage?.previewSrc}
                      alt={`Uploaded file ${index + 1}`}
                      className="object-contain"
                      layout="fill"
                    />
                    <div
                      className="absolute -top-3 -right-2 cursor-pointer rounded-full bg-white border-black"
                      onClick={() => remove(index)}
                    >
                      <IoMdCloseCircle size={20} />
                    </div>
                  </div>
                </div>

                <Field error={formState?.errors?.description} className="flex items-center flex-1">
                  <TextInput
                    name={`${name}.${index}.description`}
                    className="text-black rounded w-full"
                    register={register}
                    autoComplete="off"
                    autoCorrect="off"
                    placeholder="Image description (optional)"
                    onFocus={() => handleFocus(index)}
                    onBlur={handleBlur}
                    style={{
                      height: '35px',
                      border: isActive ? '2.5px solid black' : '1px solid #d1d5db',
                      width: '250px',
                      fontSize: '13px'
                    }}
                  />
                </Field>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

};
